import * as _ from 'lodash-es'
import { type PropsWithChildren, useEffect } from 'react'
import { Toast } from '@bridge/ui'
import UserRepository from 'app/api/UserRepository'
import { useUser } from 'app/hooks/useUser'
import LoadingSpinner from 'app/features/component/LoadingSpinner'
import { useRouter } from 'next/router'
import useAuth from 'app/hooks/useAuth'
import { PATH } from '@bridge/core/constants/route-path'

type AuthGuardProps = PropsWithChildren<{
	toast?: boolean
}>

const { updateLastLoginDate } = UserRepository

export default function AuthGuard ({ children, toast = true }: AuthGuardProps) {
	const router = useRouter()
	const { isLoading, error, isLoggedOut } = useUser()

	if (isLoading || !router.isReady) {
		return <LoadingSpinner />
	}

	if (isLoggedOut) {
		return <LoggedOut toast={toast} />
	}

	if (error) {
		// go to error boundary
		// 예기치 못한 에러
		throw error
	}

	return (
		<LoggedIn>
			{children}
		</LoggedIn>
	)
}

function LoggedOut ({ toast }: { toast: boolean }) {
	const router = useRouter()
	const { logout } = useAuth()

	useEffect(() => {
		const handleLoggedOut = async () => {
			if (toast) {
				Toast.show({ description: '로그인 이후 이용 가능합니다.' })
			}

			const isLandingToMainPage = [
				window.location.pathname === '/parent/',
				new URLSearchParams(window.location.search).get('path') === 'my-page',
			].some(_.identity)

			// 인코딩된 url을 넘겨주지 않으면 주소의 "&"부터
			// 주소창에서 없어지면서 해당 페이지로 넘어가질 않음
			const url = isLandingToMainPage
				? '/log-in?return-url=/'
				: `/log-in?return-url=${encodeURIComponent(window.location.pathname + window.location.search)}`

			await router.push(url)
		}

		handleLoggedOut().catch(console.error)
	}, [logout, router, toast])

	return null
}

function LoggedIn ({ children }: PropsWithChildren) {
	const router = useRouter()
	const { user } = useUser()

	const isDaekyoUser = user?.userDetailType === 'daekyo'
	const isInitUser = user?.gender === 'U'
	const isNeedToRouting = isDaekyoUser && isInitUser && router.pathname !== PATH.AUTH_AGREEMENT

	useEffect(() => {
		updateLastLoginDate().catch(console.error)
	}, [])

	useEffect(() => {
		if (!isNeedToRouting) {
			return
		}

		void router.replace(PATH.AUTH_AGREEMENT)
	}, [isNeedToRouting, router])

	if (isNeedToRouting) {
		return null
	}

	return <>{children}</>
}
